import { formatDeckKey, type DeckShare } from '@/config/objects/deck-shares';
import { buildDeckShareUrl } from '@/lib/deck-share-url';
import { Button, formatDateTime, toast } from '@startupquickstart/frontend';
import { Copy } from 'lucide-react';
import { useCallback } from 'react';
import { Link } from 'react-router-dom';

interface DeckShareDetailsCardProps {
  share: DeckShare;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-xs font-medium tracking-wider text-muted-foreground uppercase">
        {label}
      </div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

/**
 * Details card for a share link: the link itself (with copy and an
 * email-a-draft shortcut), who it was made for, which deck it opens, and how
 * long it stays live.
 */
export function DeckShareDetailsCard({ share }: DeckShareDetailsCardProps) {
  const shareUrl = share.shareUrl ?? buildDeckShareUrl(share.token);

  const handleCopy = useCallback(() => {
    if (!shareUrl) return;
    void navigator.clipboard.writeText(shareUrl).then(
      () => toast.success('Presentation link copied'),
      () => toast.error('Could not copy the link'),
    );
  }, [shareUrl]);

  const isLive = share.isActive && !share.revokedAt;

  return (
    <div className="rounded-xl border bg-card shadow-sm">
      <div className="flex flex-col gap-3 border-b px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-medium tracking-tight text-zinc-900">Share link</h2>
          <p className="text-sm text-muted-foreground">
            Send this to {share.recipientName} — it opens the presentation with no password
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            disabled={!shareUrl}
            className="cursor-pointer"
          >
            <Copy className="mr-2 h-4 w-4" />
            Copy link
          </Button>
        </div>
      </div>

      <div className="space-y-6 p-6">
        <Field label="Link">
          {shareUrl ? (
            <code className="block rounded bg-muted px-2 py-1 text-sm break-all whitespace-normal">
              {shareUrl}
            </code>
          ) : (
            <span className="text-muted-foreground">Not available</span>
          )}
        </Field>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Field label="Sent to">{share.recipientName}</Field>

          <Field label="Recipient email">
            {share.recipientEmail || <span className="text-muted-foreground">Not recorded</span>}
          </Field>

          <Field label="Hotel">
            {share.hotel ? (
              <Link to={`/hotels/${share.hotel.id}`} className="text-primary hover:underline">
                {share.hotel.name}
              </Link>
            ) : (
              <span className="text-muted-foreground">Not linked</span>
            )}
          </Field>

          <Field label="Deck">{formatDeckKey(share.deckKey)}</Field>

          <Field label="Status">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                isLive ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
              }`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${isLive ? 'bg-emerald-500' : 'bg-red-500'}`}
              />
              {share.revokedAt ? 'Revoked' : share.isActive ? 'Live' : 'Switched off'}
            </span>
          </Field>

          <Field label="Expires">
            {share.expiresAt ? (
              formatDateTime(share.expiresAt)
            ) : (
              <span className="text-muted-foreground">Never</span>
            )}
          </Field>

          <Field label="Created by">
            {share.createdBy?.email || <span className="text-muted-foreground">Unknown</span>}
          </Field>

          <Field label="Created">{formatDateTime(share.createdAt)}</Field>
        </div>

        {share.notes && (
          <div className="border-t pt-6">
            <Field label="Notes">
              <p className="whitespace-pre-wrap">{share.notes}</p>
            </Field>
          </div>
        )}
      </div>
    </div>
  );
}
